/*
  <tt-carousel>: carousel used by the "What Parents Say" and "What's Inside the Box" sections.

  Markup:
    [data-tt-track]                   the scrolling row of cards
    [data-tt-prev] / [data-tt-next]   buttons (move one card; never dead-end)
    img[data-src]                     card images, loaded once the carousel nears the viewport

  Attributes:
    data-mode="marquee"   continuous, infinite auto-scroll (like the product page's "Why People Love Us"):
                          the card set is repeated once and the row jumps back by one set, unseen,
                          whenever it has scrolled a full set, so it loops forever in both directions.
    data-autoplay="true"  move automatically. In marquee mode at data-speed px per 60fps frame
                          (default 2, i.e. 120px/s, time-based so it is the same on any refresh rate);
                          otherwise one card every data-interval ms (default 4000).

  Automatic movement pauses while the carousel is hovered, keyboard-focused, touched, off screen, in a
  background tab, or while something inside it dispatches "tt:pause" (e.g. an open video), and is
  disabled for users who prefer reduced motion.
*/
if (!customElements.get('tt-carousel')) {
	class TTCarousel extends HTMLElement {
		connectedCallback() {
			this.track = this.querySelector('[data-tt-track]');
			if (!this.track) return;
			this.prev = this.querySelector('[data-tt-prev]');
			this.next = this.querySelector('[data-tt-next]');
			this.marquee = this.dataset.mode === 'marquee';
			this.interval = Number(this.dataset.interval) || 4000;
			this.speed = (Number(this.dataset.speed) || 2) * 60; // px per second
			this.pauses = new Set();
			this.cards = [...this.track.children];

			this.onPrev = () => this.go(-1, true);
			this.onNext = () => this.go(1, true);
			this.onScroll = () => {
				if (this.marquee && !this.driving) this.scheduleWrap();
				if (this.ticking) return;
				this.ticking = true;
				requestAnimationFrame(() => {
					this.ticking = false;
					this.update();
				});
			};
			this.onResize = () => {
				this.measure();
				this.update();
			};

			if (this.prev) this.prev.addEventListener('click', this.onPrev);
			if (this.next) this.next.addEventListener('click', this.onNext);
			this.track.addEventListener('scroll', this.onScroll, { passive: true });

			if (this.marquee) {
				this.classList.add('tt-carousel--marquee');
				this.setupLoop();
			}

			if ('ResizeObserver' in window) {
				this.resizeObserver = new ResizeObserver(this.onResize);
				this.resizeObserver.observe(this.track);
			}

			this.update();
			this.deferImages();
			this.setupAutoplay();
		}

		disconnectedCallback() {
			if (!this.track) return;
			if (this.prev) this.prev.removeEventListener('click', this.onPrev);
			if (this.next) this.next.removeEventListener('click', this.onNext);
			this.track.removeEventListener('scroll', this.onScroll);
			if (this.resizeObserver) this.resizeObserver.disconnect();
			if (this.imageObserver) this.imageObserver.disconnect();
			if (this.visibilityObserver) this.visibilityObserver.disconnect();
			document.removeEventListener('visibilitychange', this.onVisibility);
			this.stop();
		}

		get step() {
			const card = this.track.firstElementChild;
			if (!card) return 0;
			const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
			return card.getBoundingClientRect().width + gap;
		}

		get maxScroll() {
			return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
		}

		// ----- Infinite loop (marquee mode) -----

		// Only the original cards decide whether the row overflows; clones are added only if it does.
		get overflowing() {
			if (!this.marquee || !this.clones) return this.maxScroll > 1;
			return this.setWidth > this.track.clientWidth + 1;
		}

		setupLoop() {
			// Do the originals overflow at all? (No point looping a row that fits.)
			if (this.track.scrollWidth - this.track.clientWidth <= 1) return;
			this.clones = this.cards.map((card) => {
				const clone = card.cloneNode(true);
				clone.setAttribute('aria-hidden', 'true');
				clone.dataset.ttClone = '';
				clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
				if (clone.matches('a, button, [tabindex]')) clone.setAttribute('tabindex', '-1');
				this.track.appendChild(clone);
				return clone;
			});
			this.measure();
		}

		// Width of one full card set = distance from the first card to its first clone.
		measure() {
			if (!this.clones || !this.clones.length) return;
			this.setWidth = this.clones[0].offsetLeft - this.cards[0].offsetLeft;
		}

		// Keep scrollLeft inside [0, setWidth) by jumping a whole set; the content there is identical.
		wrap() {
			if (!this.clones || !this.setWidth) return;
			const left = this.track.scrollLeft;
			if (left >= this.setWidth) this.track.scrollLeft = left - this.setWidth;
			else if (left < 1) this.track.scrollLeft = left + this.setWidth;
			this.pos = this.track.scrollLeft;
		}

		// After a manual swipe/scroll settles, wrap (never mid-gesture: that would stop the fling).
		scheduleWrap() {
			clearTimeout(this.wrapTimer);
			this.wrapTimer = setTimeout(() => {
				if (!this.pauses.has('touch') && !this.pauses.has('nav')) this.wrap();
			}, 160);
		}

		// ----- Buttons -----

		go(direction, fromUser = false) {
			if (!this.overflowing) return;
			const step = this.step;
			if (this.marquee && this.clones) {
				// Make room first (an unseen jump of one set), then glide one card.
				const left = this.track.scrollLeft;
				if (direction > 0 && left + step > this.maxScroll) this.track.scrollLeft = left - this.setWidth;
				if (direction < 0 && left - step < 0) this.track.scrollLeft = left + this.setWidth;
				this.pause('nav');
				this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
				clearTimeout(this.navTimer);
				this.navTimer = setTimeout(() => {
					this.pos = this.track.scrollLeft;
					this.wrap();
					this.resume('nav');
				}, 1500);
				return;
			}
			const max = this.maxScroll;
			const left = this.track.scrollLeft;
			let target;
			if (direction > 0) target = left >= max - 2 ? 0 : Math.min(left + step, max);
			else target = left <= 2 ? max : Math.max(left - step, 0);
			this.track.scrollTo({ left: target, behavior: 'smooth' });
			if (fromUser) this.restart();
		}

		update() {
			const overflowing = this.overflowing;
			this.classList.toggle('tt-carousel--static', !overflowing);
			if (this.prev) this.prev.disabled = !overflowing;
			if (this.next) this.next.disabled = !overflowing;
		}

		deferImages() {
			const images = this.querySelectorAll('img[data-src]');
			if (!images.length) return;
			const load = () => {
				images.forEach((img) => {
					if (img.dataset.srcset) img.srcset = img.dataset.srcset;
					img.src = img.dataset.src;
					img.removeAttribute('data-src');
					img.removeAttribute('data-srcset');
				});
			};
			const observe = () => {
				if (!('IntersectionObserver' in window)) return load();
				this.imageObserver = new IntersectionObserver((entries) => {
					if (!entries.some((entry) => entry.isIntersecting)) return;
					this.imageObserver.disconnect();
					load();
				}, { rootMargin: '400px 0px' });
				this.imageObserver.observe(this);
			};
			// Wait for the page's own load (hero image, fonts) so card images never compete with it —
			// but never longer than 3s: slow third-party scripts can delay the load event a lot.
			if (document.readyState === 'complete') return observe();
			let started = false;
			const startOnce = () => {
				if (started) return;
				started = true;
				clearTimeout(fallback);
				observe();
			};
			const fallback = setTimeout(startOnce, 3000);
			window.addEventListener('load', startOnce, { once: true });
		}

		// ----- Automatic movement -----

		setupAutoplay() {
			if (this.dataset.autoplay !== 'true') return;
			if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

			const hold = (reason) => () => this.pause(reason);
			const release = (reason) => () => this.resume(reason);
			const touchResume = this.marquee ? 1500 : 5000;

			this.addEventListener('mouseenter', hold('hover'));
			this.addEventListener('mouseleave', release('hover'));
			// Only keyboard focus pauses: a mouse click also focuses the card/button, and the
			// video dialog returns focus to the card on close, which would otherwise stop autoplay.
			this.addEventListener('focusin', (event) => {
				if (event.target.matches(':focus-visible')) this.pause('focus');
			});
			this.addEventListener('focusout', (event) => {
				if (!this.contains(event.relatedTarget)) this.resume('focus');
			});
			this.addEventListener('touchstart', () => {
				clearTimeout(this.touchTimer);
				this.pause('touch');
			}, { passive: true });
			this.addEventListener('touchend', () => {
				clearTimeout(this.touchTimer);
				this.touchTimer = setTimeout(() => this.resume('touch'), touchResume);
			}, { passive: true });
			this.addEventListener('tt:pause', hold('external'));
			this.addEventListener('tt:resume', release('external'));

			this.onVisibility = () => (document.hidden ? this.pause('tab') : this.resume('tab'));
			document.addEventListener('visibilitychange', this.onVisibility);

			this.autoplay = true;
			this.pause('offscreen');
			if ('IntersectionObserver' in window) {
				this.visibilityObserver = new IntersectionObserver(([entry]) => {
					entry.isIntersecting ? this.resume('offscreen') : this.pause('offscreen');
				}, { threshold: this.marquee ? 0.01 : 0.35 });
				this.visibilityObserver.observe(this);
			} else {
				this.resume('offscreen');
			}
		}

		pause(reason) {
			this.pauses.add(reason);
			this.stop();
		}

		resume(reason) {
			this.pauses.delete(reason);
			if (!this.pauses.size && this.autoplay) this.start();
		}

		start() {
			this.stop();
			if (!this.overflowing) return;
			if (!this.marquee) {
				this.timer = setInterval(() => this.go(1), this.interval);
				return;
			}
			// Continuous scroll from wherever the row is now (it may have been swiped or clicked).
			this.pos = this.track.scrollLeft;
			let last = performance.now();
			const frame = (now) => {
				const dt = Math.min(0.05, (now - last) / 1000); // cap: no big jump after a stalled frame
				last = now;
				this.pos += this.speed * dt;
				if (this.setWidth && this.pos >= this.setWidth) this.pos -= this.setWidth;
				this.driving = true;
				this.track.scrollLeft = this.pos;
				this.driving = false;
				this.raf = requestAnimationFrame(frame);
			};
			this.raf = requestAnimationFrame(frame);
		}

		stop() {
			clearInterval(this.timer);
			this.timer = null;
			cancelAnimationFrame(this.raf);
			this.raf = null;
		}

		// After a manual move, wait a full interval before auto-advancing again.
		restart() {
			if (this.timer) this.start();
		}
	}

	customElements.define('tt-carousel', TTCarousel);
}
